import axios from 'axios';
import { previewMode } from '../previewMode';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api',
  timeout: 30000,
});

if (process.env.NODE_ENV === 'development' && previewMode) {
  api.defaults.adapter = require('../previewAdapter').default;
}

// Request interceptor - add token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url.includes('/auth/login')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

export const authApi = {
  login: (data) => api.post('/admin/auth/login', data),
  me: () => api.get('/admin/auth/me'),
};

export const shopApi = {
  list: () => api.get('/admin/shops'),
  create: (data) => api.post('/admin/shops', data),
  update: (id, data) => api.put(`/admin/shops/${id}`, data),
  uploadImage: (id, data) => api.post(`/admin/shops/${id}/image`, data),
  removeImage: (id) => api.delete(`/admin/shops/${id}/image`),
  testSaved: (id) => api.post(`/admin/shops/${id}/test-connection`),
  testConfig: (data) => api.post('/admin/shops/test-connection', data),
};

export const memberApi = {
  list: (params) => api.get('/admin/members', { params }),
  create: (data) => api.post('/admin/members', data),
  get: (id) => api.get(`/admin/members/${id}`),
  update: (id, data) => api.put(`/admin/members/${id}`, data),
  retryShops: (id) => api.post(`/admin/members/${id}/retry-ar`),
  ledger: (id, params) => api.get(`/admin/members/${id}/ledger`, { params }),
  adjust: (id, data) => api.post(`/admin/members/${id}/adjust`, data),
  updateShopMap: (id, data) => api.put(`/admin/members/${id}/shop-map`, data),
  unlinkLine: (id) => api.post(`/admin/members/${id}/unlink-line`, { confirm: true }),
  audit: (id, params) => api.get(`/admin/members/${id}/audit`, { params }),
};

export const adminUserApi = {
  list: () => api.get('/admin/users'),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.patch(`/admin/users/${id}`, data),
  audit: (params = {}) => api.get('/admin/users/audit', { params }),
};

export const campaignApi = {
  list: () => api.get('/admin/campaigns'),
  shops: () => api.get('/admin/campaigns/shops'),
  arTypes: () => api.get('/admin/campaigns/ar-types'),
  create: (data) => api.post('/admin/campaigns', data),
  update: (id, data) => api.put(`/admin/campaigns/${id}`, data),
  uploadImage: (formData) => api.post('/admin/campaigns/upload-image', formData),
  push: (id, data) => api.post(`/admin/campaigns/${id}/push`, data),
  pushes: (id) => api.get(`/admin/campaigns/${id}/pushes`),
  audience: (id, type) => api.get(`/admin/campaigns/${id}/audience`, { params: { type } }),
};

export const couponApi = {
  list: (params) => api.get('/admin/coupons', { params }),
  events: (id) => api.get(`/admin/coupons/${id}/events`),
  refund: (id) => api.post(`/admin/coupons/${id}/refund`),
  retry: (id) => api.post(`/admin/coupons/${id}/retry`),
};

export const syncApi = {
  status: () => api.get('/admin/sync/status'),
  run: (shopId) => api.post(`/admin/sync/run/${shopId}`),
  runAll: () => api.post('/admin/sync/run-all'),
  reconcileCoupons: () => api.post('/admin/sync/reconcile-coupons'),
  reconcilePoints: () => api.post('/admin/sync/reconcile-points'),
  actions: () => api.get('/admin/sync/actions'),
  expirePoints: () => api.post('/admin/sync/expire-points'),
  dashboard: () => api.get('/admin/sync/dashboard'),
};
